import { Palette, Type, Sparkles } from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Card from '@/components/ui/Card';
import { KNOB_VALUES } from '@/looks';
import type {
  ButtonShape,
  CardStyle,
  Density,
  FooterVariant,
  HeaderVariant,
  HeroVariant,
  LookOverrides,
  ProductCardVariant,
  RadiusScale,
} from '@/looks';
import type { DesignThemeConfig } from '@/stores/design-preview-store';
import ContrastAudit from '@/components/admin/ContrastAudit';
import FieldLabel from '@/components/ui/FieldLabel';

/**
 * Shared theme fine-tune controls: the runtime-overridable Colors, Fonts, and
 * (when a Look is selected) per-knob Look overrides. Reused by both the admin
 * Theme Customizer and the Design Sandbox so the two stay in lockstep.
 *
 * Fully controlled: reads `value` (a theme_config) and emits the next
 * theme_config via `onChange`. The Look *preset* picker (the gallery grid) is
 * intentionally NOT here — each host renders its own gallery — but the advanced
 * per-knob overrides are, since they're identical in both places.
 */

// The option VALUES come from the registry's `KNOB_VALUES` table (one source
// for the storefront resolver, the wizard and this UI); only the human labels
// live here. A variant added to the registry without a label fails to compile.
function knobOptions<T extends string>(values: readonly T[], labels: Record<T, string>) {
  return values.map((value) => ({ label: labels[value], value }));
}
const HEADER_VARIANT_OPTIONS = knobOptions<HeaderVariant>(KNOB_VALUES.header_variant, {
  centered_logo: 'Centered logo',
  classic: 'Classic (logo left + inline nav)',
  transparent_overlay: 'Transparent overlay',
  mega_menu: 'Mega menu',
  minimal: 'Minimal',
  floating_glass: 'Floating glass (transparent, frosts on scroll)',
});
const HERO_VARIANT_OPTIONS = knobOptions<HeroVariant>(KNOB_VALUES.hero_variant, {
  standard: 'Standard (image background)',
  split: 'Split (image + content)',
  fullscreen_media: 'Fullscreen media',
  centered_card: 'Centered card',
  search_forward: 'Search forward (lodging)',
  none: 'None (skip hero)',
});
const FOOTER_VARIANT_OPTIONS = knobOptions<FooterVariant>(KNOB_VALUES.footer_variant, {
  multi_column: 'Multi column',
  compact: 'Compact',
  big_cta: 'Big CTA banner',
  minimal: 'Minimal',
  app_footer: 'App footer (badges + columns, follows the theme)',
});
const BUTTON_SHAPE_OPTIONS = knobOptions<ButtonShape>(KNOB_VALUES.button_shape, {
  rounded: 'Rounded',
  pill: 'Pill',
  square: 'Square',
  cut_corner: 'Cut corner',
});
const CARD_STYLE_OPTIONS = knobOptions<CardStyle>(KNOB_VALUES.card_style, {
  elevated: 'Elevated (shadow)',
  bordered: 'Bordered',
  flat: 'Flat',
  glass: 'Glass',
});
const RADIUS_SCALE_OPTIONS = knobOptions<RadiusScale>(KNOB_VALUES.radius_scale, {
  sharp: 'Sharp (2-4px)',
  soft: 'Soft (default)',
  round: 'Round',
  pill: 'Pill (interactive only)',
});
const DENSITY_OPTIONS = knobOptions<Density>(KNOB_VALUES.density, {
  compact: 'Compact',
  comfortable: 'Comfortable (default)',
  spacious: 'Spacious',
});
const PRODUCT_CARD_OPTIONS = knobOptions<ProductCardVariant>(KNOB_VALUES.product_card_variant, {
  standard: 'Standard (default)',
  minimal: 'Minimal (image, name, price)',
  overlay: 'Overlay (name + price on the photo)',
});

export const FONT_OPTIONS = [
  { label: 'Playfair Display', value: 'Playfair Display' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Merriweather', value: 'Merriweather' },
  { label: 'DM Serif Display', value: 'DM Serif Display' },
  { label: 'Fraunces', value: 'Fraunces' },
  { label: 'Cormorant Garamond', value: 'Cormorant Garamond' },
  { label: 'Space Grotesk', value: 'Space Grotesk' },
  { label: 'Manrope', value: 'Manrope' },
  { label: 'Fredoka', value: 'Fredoka' },
];

export const BODY_FONT_OPTIONS = [
  { label: 'Inter', value: 'Inter' },
  { label: 'System UI', value: 'system-ui' },
  { label: 'Roboto', value: 'Roboto' },
  { label: 'DM Sans', value: 'DM Sans' },
  { label: 'Nunito', value: 'Nunito' },
  { label: 'Source Sans 3', value: 'Source Sans 3' },
  { label: 'Manrope', value: 'Manrope' },
];

/**
 * One row of the "Customize this Look" advanced section. Reads/writes a single
 * key on `value.look.overrides`. Empty value = preset default (the key is
 * removed from overrides entirely so resolveLook falls back to the preset).
 */
function OverrideSelect({
  label,
  overrideKey,
  options,
  value,
  onChange,
}: {
  label: string;
  overrideKey: keyof LookOverrides;
  options: { label: string; value: string }[];
  value: DesignThemeConfig;
  onChange: (next: DesignThemeConfig) => void;
}) {
  const presetId = value.look?.preset_id;
  if (!presetId) return null;
  const currentOverrides = value.look?.overrides ?? {};
  const currentValue = (currentOverrides as Record<string, string | undefined>)[overrideKey] ?? '';
  return (
    <Select
      label={label}
      value={currentValue}
      onChange={(e) => {
        const next = { ...currentOverrides } as Record<string, string | undefined>;
        if (e.target.value) {
          next[overrideKey] = e.target.value;
        } else {
          delete next[overrideKey];
        }
        const hasAny = Object.keys(next).length > 0;
        onChange({
          ...value,
          look: hasAny
            ? { preset_id: presetId, overrides: next as LookOverrides }
            : { preset_id: presetId },
        });
      }}
      options={[{ label: 'Preset default', value: '' }, ...options]}
    />
  );
}

interface ThemeFineTuneControlsProps {
  value: DesignThemeConfig;
  onChange: (next: DesignThemeConfig) => void;
  /** Show the "Generate with AI" button in the Colors header. */
  showAiButton?: boolean;
  onGenerateAiClick?: () => void;
}

export default function ThemeFineTuneControls({
  value,
  onChange,
  showAiButton,
  onGenerateAiClick,
}: ThemeFineTuneControlsProps) {
  const hasOverrides = value.look?.overrides && Object.keys(value.look.overrides).length > 0;

  return (
    <div className="space-y-6">
      {/* Advanced per-knob Look overrides (only when a Look is selected). */}
      {value.look && (
        <Card padding="lg">
          <details>
            <summary className="text-sm font-medium text-gray-700 cursor-pointer select-none">
              Customize this Look (advanced)
            </summary>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <OverrideSelect label="Header" overrideKey="header_variant" options={HEADER_VARIANT_OPTIONS} value={value} onChange={onChange} />
              <OverrideSelect label="Hero" overrideKey="hero_variant" options={HERO_VARIANT_OPTIONS} value={value} onChange={onChange} />
              <OverrideSelect label="Footer" overrideKey="footer_variant" options={FOOTER_VARIANT_OPTIONS} value={value} onChange={onChange} />
              <OverrideSelect label="Button shape" overrideKey="button_shape" options={BUTTON_SHAPE_OPTIONS} value={value} onChange={onChange} />
              <OverrideSelect label="Card style" overrideKey="card_style" options={CARD_STYLE_OPTIONS} value={value} onChange={onChange} />
              <OverrideSelect label="Corner radius" overrideKey="radius_scale" options={RADIUS_SCALE_OPTIONS} value={value} onChange={onChange} />
              <OverrideSelect label="Density" overrideKey="density" options={DENSITY_OPTIONS} value={value} onChange={onChange} />
              <OverrideSelect label="Product card" overrideKey="product_card_variant" options={PRODUCT_CARD_OPTIONS} value={value} onChange={onChange} />
            </div>
            {hasOverrides && (
              <button
                type="button"
                onClick={() => onChange({ ...value, look: { preset_id: value.look!.preset_id } })}
                className="mt-3 text-xs text-green-700 hover:text-green-800 underline"
              >
                Reset overrides to preset defaults
              </button>
            )}
          </details>
        </Card>
      )}

      {/* Colors */}
      <Card padding="lg">
        <div className="flex items-center gap-2 mb-4">
          <Palette className="h-5 w-5 text-gray-500" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-gray-900">Colors</h2>
          {showAiButton && (
            <Button variant="outline" size="sm" onClick={onGenerateAiClick}>
              <Sparkles className="w-4 h-4 mr-1" />
              Generate with AI
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <FieldLabel help="Your main brand colour. Drives buttons, links, the header strip and the footer band. Pick something dark enough that white button text stays readable — the contrast panel below tells you.">Primary Color</FieldLabel>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={value.primary_color || '#000000'}
                onChange={(e) => onChange({ ...value, primary_color: e.target.value })}
                className="h-10 w-10 rounded border border-gray-300 cursor-pointer"
                aria-label="Primary color"
              />
              <Input value={value.primary_color || ''} onChange={(e) => onChange({ ...value, primary_color: e.target.value })} label="" />
            </div>
          </div>
          <div>
            <FieldLabel help="Used sparingly for highlights: sale badges, active states, the occasional callout. It should stand out against the primary colour rather than harmonise with it.">Accent Color</FieldLabel>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={value.accent_color || '#000000'}
                onChange={(e) => onChange({ ...value, accent_color: e.target.value })}
                className="h-10 w-10 rounded border border-gray-300 cursor-pointer"
                aria-label="Accent color"
              />
              <Input value={value.accent_color || ''} onChange={(e) => onChange({ ...value, accent_color: e.target.value })} label="" />
            </div>
          </div>
          <div>
            <FieldLabel help="The off-white page background behind most content. Keep it light — body text sits directly on it, and a dark value here makes the whole site hard to read.">Warm Background</FieldLabel>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={value.bg_warm || '#000000'}
                onChange={(e) => onChange({ ...value, bg_warm: e.target.value })}
                className="h-10 w-10 rounded border border-gray-300 cursor-pointer"
                aria-label="Warm background color"
              />
              <Input value={value.bg_warm || ''} onChange={(e) => onChange({ ...value, bg_warm: e.target.value })} label="" />
            </div>
          </div>
        </div>

        {/* Live WCAG AA check on the three colours above. Lives here rather
            than in ThemeCustomizer so the Design Sandbox gets it too — this
            control is shared precisely so the two surfaces cannot drift. */}
        <ContrastAudit
          className="mt-4"
          colors={{
            primary_color: value.primary_color,
            accent_color: value.accent_color,
            bg_warm: value.bg_warm,
          }}
        />
      </Card>

      {/* Fonts */}
      <Card padding="lg">
        <div className="flex items-center gap-2 mb-4">
          <Type className="h-5 w-5 text-gray-500" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-gray-900">Typography</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Heading Font"
            hint="Used for page titles and section headings. Display faces look best large — check a product page, not just this preview."
            value={value.font_heading || ''}
            onChange={(e) => onChange({ ...value, font_heading: e.target.value })}
            options={FONT_OPTIONS}
          />
          <Select
            label="Body Font"
            hint="Everything else: product descriptions, buttons, navigation. Favour readability over personality here."
            value={value.font_body || ''}
            onChange={(e) => onChange({ ...value, font_body: e.target.value })}
            options={BODY_FONT_OPTIONS}
          />
        </div>
      </Card>
    </div>
  );
}
